<template>
  <footer class="bg-gray-900 text-gray-300">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
        <!-- Company Info -->
        <div class="space-y-6">
          <div class="flex items-center">
            <span class="text-2xl font-heading text-white">Heidelbell</span>
          </div>
          <p class="text-gray-400">
            {{ $t('about.description') }}
          </p>
          <div class="flex space-x-4">
            <a v-for="(social, index) in socialLinks" :key="index"
               :href="social.href"
               class="text-gray-400">
              <component :is="social.icon" class="w-5 h-5"></component>
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div>
          <h3 class="text-white font-semibold mb-6">Quick Links</h3>
          <ul class="space-y-3">
            <li v-for="(link, index) in quickLinks" :key="index">
              <router-link :to="link.href" 
                         class="text-gray-400">
                {{ link.label }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Contact Info -->
        <div>
          <h3 class="text-white font-semibold mb-6">{{ $t('contact.subtitle') }}</h3>
          <ul class="space-y-4">
            <!-- Address commented out
            <li class="flex items-start space-x-3">
              <svg class="w-5 h-5 text-primary mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              <span class="text-gray-400">{{ $t('contact.info.address.value') }}</span>
            </li>
            -->
            <li class="flex items-start space-x-3">
              <svg class="w-5 h-5 text-primary mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              <span class="text-gray-400">{{ $t('contact.info.officeAddress.value') }}</span>
            </li>
            <li class="flex items-start space-x-3">
              <svg class="w-5 h-5 text-primary mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              <a href="mailto:connect@heidelbell.com" class="text-gray-400">
                connect@heidelbell.com
              </a>
            </li>
            <li class="flex items-start space-x-3">
              <svg class="w-5 h-5 text-primary mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
              </svg>
              <a :href="`tel:${$t('contact.info.phone.value').replace(/\s/g, '')}`" class="text-gray-400">
                {{ $t('contact.info.phone.value') }}
              </a>
            </li>
            <li class="flex items-start space-x-3">
              <svg class="w-5 h-5 text-primary mt-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <a :href="`https://wa.me/${$t('contact.info.whatsapp.value').replace(/\s/g, '').replace(/\+/g, '')}`" target="_blank" rel="noopener noreferrer" class="text-gray-400">
                {{ $t('contact.info.whatsapp.value') }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="border-t border-gray-800 mt-12 pt-8">
        <div class="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          <div class="flex-1 md:flex-none text-center md:text-left space-y-2">
            <p class="text-gray-400 text-sm whitespace-normal">
              {{ $t('footer.copyright') }}
            </p>
            <p class="text-gray-400 text-sm whitespace-normal">
              {{ $t('footer.bottom') }}
            </p>
          </div>
          <div class="flex space-x-6 flex-shrink-0">
            <router-link to="/privacy" class="text-gray-400 text-sm">
              {{ $t('footer.links.privacy') }}
            </router-link>
            <router-link to="/terms" class="text-gray-400 text-sm">
              {{ $t('footer.links.terms') }}
            </router-link>
            <router-link to="/imprint" class="text-gray-400 text-sm">
              {{ $t('footer.links.imprint') }}
            </router-link>
            <router-link to="/attributions" class="text-gray-400 text-sm">
              {{ $t('footer.links.attributions') }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Social Media Links
const socialLinks = [
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/98654211/',
    icon: 'LinkedInIcon'
  }
]

// Quick Links
const quickLinks = [
  { label: t('nav.home'), href: '/' },
  { label: t('nav.about'), href: '/about' },
  { label: t('nav.services'), href: '/services' },
  { label: t('nav.contact'), href: '/contact' }
]
</script>

<style scoped>
/* Social Icons */
.LinkedInIcon {
  @apply w-5 h-5;
}
</style> 